import { z } from 'zod'
import './registry'
import { IsoDateSchema, MoneySchema, ShortIdSchema, TenantIdSchema, UlidSchema } from './common'
import {
  OnlineImagesSchema,
  RecordConditionSchema,
  SleeveConditionSchema,
  type OnlineImages,
  type Record as CatalogueRecord,
} from './records'
import { DiscogsVideoSchema, ReleaseTrackSchema } from './discogs'

/**
 * PUBLIC storefront projections (webshop plan §1, api plan Phase 2).
 *
 * These schemas define what the anonymous `/shop/*` surface returns. They are
 * deliberately NOT `RecordSchema`: that entity carries fields no customer may
 * ever see (supplier, share percentage, physical slot, internal photoKey,
 * Discogs listing state, reservation holds). `toShopRecord` below is the one
 * projection from the internal entity to this public shape — an explicit pick,
 * so an omission is a decision and never an accident.
 *
 * The shape mirrors `webshop/app/types/record.ts`, which was the frozen
 * contract the storefront was built against; that file re-exports the types
 * inferred here so the two can never drift.
 */

/**
 * The seller behind a record — enough to attribute and link it, nothing more.
 *
 * `id` is the tenantId. Safe to expose: it names a *seller*, which is public
 * information on any marketplace (and DSA seller-traceability will require it
 * once the aggregator hosts third parties). It is NOT an access-control token —
 * the public API resolves tenant from the `Host` header or the platform
 * projection, never from anything a client sends back. If a request parameter
 * ever accepts this value, that is the bug.
 */
export const ShopSummarySchema = z
  .object({
    id: TenantIdSchema,
    /** Display name, e.g. "Random Vinyl". */
    name: z.string().min(1).max(120),
    /** URL segment for the aggregator's shop profile page (`/winkel/<slug>`). */
    slug: z.string().regex(/^[a-z0-9-]+$/, 'lowercase url slug'),
  })
  .openapi('ShopSummary')

/**
 * Public availability. The internal `RecordStatus` union collapses to two
 * customer-visible states: a shopper never learns a copy is merely reserved,
 * and sold pages stay live (200) rather than 404ing.
 */
export const ShopAvailabilitySchema = z.enum(['available', 'sold']).openapi('ShopAvailability')

export const ShopRecordSchema = z
  .object({
    id: UlidSchema,
    /** Short human code (also the tail of the storefront slug). */
    shortId: ShortIdSchema,
    /** Which shop sells this copy — stamped server-side, one value per tenant response. */
    shop: ShopSummarySchema,
    artist: z.string(),
    title: z.string(),
    /** The mix/version line ("The Escape House Mix"). */
    subtitle: z.string().nullable(),
    label: z.string().nullable(),
    catno: z.string().nullable(),
    year: z.number().int().nullable(),
    country: z.string().nullable(),
    genre: z.string().nullable(),
    /** LP / 2×LP / 12" / 7" — the browse facet. */
    format: z.string().nullable(),
    /** Media (disc) grade. */
    condition: RecordConditionSchema,
    sleeveCondition: SleeveConditionSchema,
    /** Decimal string, incl. VAT — consumer prices are always shown gross (plan §10.1). */
    price: MoneySchema,
    /** Sealed/unplayed at intake. Drives VAT treatment; NOT the "new arrival" flag. */
    isNew: z.boolean(),
    availability: ShopAvailabilitySchema,
    /**
     * Listen block (detail endpoint only): the record's at-intake YouTube
     * snapshot + tracklist. The LIST omits both keys entirely (payload — the
     * browse grid never plays anything); `.default([])` keeps such a payload
     * VALID for anyone who parses it, but the storefront's browse fetch is a
     * typed cast, not a parse, so there they really are absent. The storefront
     * validates every uri (host + id) before an embed src is ever built.
     */
    videos: z.array(DiscogsVideoSchema).max(30).default([]),
    tracklist: z.array(ReleaseTrackSchema).max(150).default([]),
    /** The blurp — the shop's personal note about this record. */
    note: z.string().nullable(),
    /** Condition/provenance detail about this specific copy. */
    description: z.string().nullable(),
    /** Customer-facing photos; values are media-bucket keys the storefront resolves via its CDN base. */
    images: OnlineImagesSchema,
    /** When the copy went online — drives "net binnen deze week". */
    publishedAt: IsoDateSchema,
  })
  .openapi('ShopRecord')

export const GenreSummarySchema = z
  .object({
    name: z.string(),
    count: z.number().int().nonnegative(),
  })
  .openapi('GenreSummary')

/**
 * Project an internal catalogue record onto the public storefront shape.
 *
 * Callers must only pass PUBLISHED records (`webshopPublishedAt != null`) —
 * filtering is the query's job; projecting an unpublished record is a
 * programmer error and throws rather than leaking a half-formed page.
 *
 * `availability` collapses everything that isn't plainly buyable to `sold`:
 * a reserved copy (till basket, customer hold, or webshop checkout hold) is
 * shown as sold rather than inviting a checkout that is guaranteed to lose the
 * §5 conditional write; if the hold releases, the next cache refresh flips it
 * back. Correctness never depends on this value (ADR-0001 — the checkout
 * guard decides), so optimistic staleness in either direction is acceptable.
 */
export function toShopRecord(record: CatalogueRecord, shop: ShopSummary): ShopRecordListItem {
  if (record.webshopPublishedAt === null) {
    throw new Error(`toShopRecord: record ${record.id} is not webshop-published`)
  }
  // The return type is the projection MINUS the listen block, so TypeScript
  // checks this literal for a missing or misspelled field — the whole point
  // of the projection ("an omission is a decision, never an accident"). It is
  // a distinct TYPE rather than a runtime flag plus a cast: a cast would
  // switch that checking off for exactly the two fields whose absence is the
  // interesting part.
  return {
    id: record.id,
    shortId: record.shortId,
    shop,
    artist: record.artist,
    title: record.title,
    subtitle: record.subtitle,
    label: record.label,
    catno: record.catno,
    year: record.year,
    country: record.country,
    genre: record.genre,
    format: record.format,
    condition: record.condition,
    sleeveCondition: record.sleeveCondition,
    price: record.price,
    isNew: record.isNew,
    availability: record.status === 'in_stock' ? 'available' : 'sold',
    note: record.note,
    description: record.description,
    images: record.onlineImages,
    publishedAt: record.webshopPublishedAt,
  }
}

/**
 * The DETAIL projection: the browse item plus the listen snapshot. Separate
 * from `toShopRecord` because the browse payload deliberately omits both keys
 * — the grid never plays anything, and the list-response leak test pins their
 * absence. They carry `.default([])`, so an absent key is valid wire INPUT,
 * but nothing on the storefront re-parses the browse payload today
 * (webshop/app/api/catalogue.ts casts it), so treat a list record's
 * `videos`/`tracklist` as absent at runtime rather than `[]`.
 */
export function toShopRecordDetail(record: CatalogueRecord, shop: ShopSummary): ShopRecord {
  return {
    ...toShopRecord(record, shop),
    videos: record.videos ?? [],
    tracklist: record.tracklist ?? [],
  }
}

/** A browse-grid record: the full projection minus the detail-only listen block. */
export type ShopRecordListItem = Omit<ShopRecord, 'videos' | 'tracklist'>

export type ShopSummary = z.infer<typeof ShopSummarySchema>
export type ShopAvailability = z.infer<typeof ShopAvailabilitySchema>
export type ShopRecord = z.infer<typeof ShopRecordSchema>
/** The storefront's name for the images block — structurally `OnlineImages`. */
export type ShopRecordImages = OnlineImages
export type GenreSummary = z.infer<typeof GenreSummarySchema>
